/**
 * Automations — content bundle for the WAC UBP calculator.
 *
 * Source of truth: Confluence "Automation: Content spec for WAC Calculator"
 * https://hello.atlassian.net/wiki/spaces/acbp/pages/7129271516
 *
 * Content DRI: Belle Craig · Meter CD: Lauren Ryan
 *
 * Fields we haven't finalized are simply OMITTED from this file (most
 * are optional at the type level) so nothing renders for them. Do NOT
 * stub placeholder strings — omit the field entirely instead. There is
 * no `PLACEHOLDER` / `todoCopy` helper exported from this module by
 * design.
 *
 * DO NOT edit unrelated meter files when updating this one — the atomic
 * per-meter file split exists to prevent merge conflicts between authors.
 */

import { wacToken } from '@/libraries/design/foundations';

import type { MeterContent } from './meter-content.types';

export const AUTOMATIONS_CONTENT: MeterContent = {
	description:
		'Give your team valuable time back by reducing repetitive manual work with automation flows.',
	needsStep: {
		// Step 2 "Do you know your usage?" question — Automations copy.
		doYouKnowQuestion: 'Know how many Automation steps you\u2019ll need?',
	},
	estimateStep: {
		subtitle:
			'Based on what you\u2019ve told us, here\u2019s a summary of how many Automation steps you need, and how you can use them.',
		compareCta: 'Explore plans',
		// TEMPORARY: All meters currently share this Orange Logic asset
		// while the content team finalizes per-meter hero imagery.
		image: {
			src: 'https://dam-cdn.atl.orangelogic.com/AssetLink/bb1q20l5xjp2i85j55ou317euquo3rv3.png',
			alt: '',
			// eslint-disable-next-line local/ensure-wac-token-usage
			imageBackgroundColor: wacToken('color.extended.blue100'),
		},
		// ContentBlock copy per revised design direction (2026-07-16):
		// reframe Card 1 around ROI + built-in allowances with the
		// "Explore plans" CTA. Shared across all non-CSR meters.
		contentBlock: {
			title: 'Maximize your ROI with flexible, usage-based plans',
			intro:
				'With Atlassian collections and apps, built-in allowances align your costs with your real-world usage.',
			listItems: ['Optimize your spend', 'Plan with confidence', 'Do more with less'],
			href: '/collections',
		},
	},
	allowanceStep: {
		// Step 4 primary CTA — per Automation Steps Confluence spec.
		// Overrides the generic "Next" with a comparison-focused verb.
		nextCta: 'Compare',
	},
	summaryStep: {
		// Step 5 header — per Automations Confluence spec.
		title: 'Review usage estimate and compare',
		subtitle:
			'Based on what you\u2019ve told us, here\u2019s a summary of how many Automation steps you need, and how you can use them.',
		orgNeedsCard: {
			eyebrow: 'YOUR ORGANIZATION NEEDS',
			headlineSubtext: 'Automation steps per month',
		},
		whatsCoveredCard: {
			eyebrow: 'WHAT’S COVERED',
			headlineSubtext: 'Automation steps included monthly',
			bodyCovered: 'Your Automation steps are covered within the allowance.',
			bodyOverage:
				'There’s a gap of {overage} Automation steps. You may incur extra usage charges of {cost}.',
			listItemsCovered: [
				'Higher plans provide more allowances',
				'Track your extra usage via your capabilities subscription details page in admin hub',
				'Set an extra usage limit to stay in budget',
			],
			listItemsOverage: [
				'Higher plans provide more allowances',
				'Track your extra usage via your capabilities subscription details page in admin hub',
				'Set an extra usage limit to stay in budget',
			],
		},
		// Automations spec: share button label is "Share with team" instead
		// of the default "Copy URL to share".
		shareCta: 'Share with team',
	},
	usageProfiles: {
		exploring: {
			label: 'Experimenting',
			description:
				'Best for automating simple tasks for one app such as Jira, or a single space or team.',
			// usageRange omitted \u2014 Automations bullets already convey the range.
			list: ['1\u201310 steps run per seat per month', '1\u20135 flows created'],
			valueProp:
				'Best for automating individual tasks and simple team alerts. Focuses on reducing "busy work" like status updates and basic notifications.',
		},
		adopting: {
			label: 'Adopting',
			description: 'Best for automating team collaboration across your Atlassian system of work.',
			// usageRange omitted \u2014 Automations bullets already convey the range.
			list: ['11\u2013200 steps run per seat per month', '6\u201350 flows created'],
			valueProp:
				'Designed for teams scaling automation across multiple projects. Includes multi-step flows, cross-product flows, and standardising team rituals.',
		},
		scaling: {
			label: 'Scaling',
			description:
				'Best for driving organizational efficiency and democratizing automation for all teams.',
			// usageRange omitted \u2014 Automations bullets already convey the range.
			list: ['200+ steps run per seat per month', '60+ flows created'],
			valueProp:
				'For organizations running complex business processes at scale. Supports advanced orchestration, global rules, and high-frequency syncs across the entire enterprise.',
		},
	},
	valueProps: [
		{
			title: 'Automate everyday tasks',
			description:
				'Use automation steps to handle repetitive work like updating fields, sending notifications, creating recurring pages, and keeping issues moving through your workflow.',
		},
		{
			title: 'Connect workflows across tools',
			description:
				'Build multi-step rules that sync work across Jira, Confluence, Loom, and third-party tools \u2014 so teams stay aligned without manual follow-up.',
		},
		{
			title: 'Orchestrate business processes at scale',
			description:
				'Support complex, high-volume processes with branching logic, global rules, and AI-powered steps that help teams summarize, route, and respond to work faster.',
		},
	],
};
